<template>
  <div class="navbar-engine-controls">
    <!-- Strategic engine: recalculate all scenarios -->
    <recalculate-all-button hide-text />

    <!-- Cross-elasticity engine: runs on the first candidate scenario.
         Only shown when the enablePriceOptimisation feature flag is active. -->
    <feature-toggle :toggle="enablePriceOptimisation">
      <feature-toggle :toggle="displayPriceOptimisationButtonOnlyForPriceList" :inverted="true">
        <v-tooltip bottom content-class="engine-tooltip">
          <template #activator="{ on, attrs }">
            <!-- span wrapper required so hover fires even when button is disabled -->
            <span v-bind="attrs" v-on="on">
              <v-btn
                depressed
                :loading="calculatingScenarioElasticityResults"
                :disabled="!canRunOptimisation"
                class="refresh-button cross-elasticity-button"
                data-dd-action-name="Navbar cross-elasticity run"
                @click="runCrossElasticityForCandidateScenario"
              >
                <circles-play-icon />
              </v-btn>
            </span>
          </template>
          <div>{{ $t('gridView.tooltips.crossElasticityOptimiser') }}</div>
          <template v-if="crossElasticityButtonBlockedMsg">
            <v-divider class="my-2" dark />
            <div>{{ crossElasticityButtonBlockedMsg }}</div>
          </template>
        </v-tooltip>
      </feature-toggle>
    </feature-toggle>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { get } from 'lodash';
import engineControlsMixin from '../mixins/engineControls';

export default {
  name: 'NavbarEngineControls',

  mixins: [engineControlsMixin],

  computed: {
    ...mapGetters('gridView', ['hasUnsavedKpiResultsForWorkpackage']),
    ...mapGetters('scenarioMetadata', ['getFirstCandidateScenario']),

    candidateScenarioKey() {
      // Used for running cross-elasticity engine on the first candidate scenario, given XEO does not currently
      // have workpackage-level implementation to execute multiple scenarios at once and this will mostly be used by workpackages with a single unit-category-scenario combination.
      // If no candidate scenario is found, this will be null, which disables the button and shows an appropriate tooltip message.
      return get(this.getFirstCandidateScenario, 'scenarioKey', null);
    },

    canRunOptimisation() {
      return (
        this.isEditable &&
        !this.isWeeklyWorkpackage &&
        !this.hasUnsavedKpiResultsForWorkpackage &&
        !this.calculatingScenarioResults &&
        !this.recalculatingAll &&
        this.hasAOExtracts &&
        !!this.candidateScenarioKey
      );
    },

    crossElasticityButtonBlockedMsg() {
      if (this.isWeeklyWorkpackage) {
        return this.$t('gridView.tooltips.notAllowedInWeeklyWorkpackage');
      }
      if (this.hasUnsavedKpiResultsForWorkpackage) {
        return this.$t('gridView.tooltips.hasUnsavedKpiResults');
      }
      if (this.calculatingScenarioResults || this.recalculatingAll) {
        return this.$t('gridView.tooltips.engineRunning');
      }
      if (!this.hasAOExtracts) {
        return this.$t('gridView.tooltips.missingAOExtracts');
      }
      if (!this.candidateScenarioKey) {
        return this.$t('gridView.tooltips.noCandidateScenario');
      }
      return '';
    },
  },

  methods: {
    runCrossElasticityForCandidateScenario() {
      this.handleCrossElasticityOptimisation({
        scenarioKey: this.candidateScenarioKey,
        workpackageId: this.selectedWorkpackage._id,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.navbar-engine-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.refresh-button {
  background-color: $pricing-primary !important;
  color: $pricing-white;
  min-width: unset !important;
  padding: unset !important;
  height: 3rem !important;
  width: 3rem;
}

.cross-elasticity-button {
  background-color: $pricing-blue !important;
}
</style>
